import React, { useState } from 'react';
import { dayjs } from 'shared/utils/date';
import { FormattedMessage } from 'react-intl';
import Color from 'color';
import { percent } from 'shared/constants/math';
import { getSegmentDateRangeColor, SEGMENT_COLORS } from 'shared/constants/colors';
import { formatDateRange } from 'shared/constants/date';
import { Switch, Button, Progress, Tooltip } from 'antd';
import { CalendarOutlined, GlobalOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons';
import MatomoIcon from 'assets/images/matomo-icon.png';

import globalMessages from 'translations/messages';
import messages from '../messages';
import './filter-segment.less';

export const MAX_ENABLED_SEGMENTS = 6;

const Segment = ({
  segment,
  dateRanges,
  filterResponseCounts,
  onEdit,
  onToggle,
  switchDisabled,
  switchTooltip,
  isStaticSegment = false,
}) => {
  const { id, friendlyTitle, responseCounts = [], sortIndex, isEnabled, shared, createdByEmail } = segment;

  const [toggling, setToggling] = useState(false);
  const toggleSegment = async (value) => {
    setToggling(true);
    await onToggle(id, value);
    setToggling(false);
  };

  const getPercent = (count, total) => {
    const progress = percent(count, total || 1);
    return Math.round(progress);
  };

  const getProgressbarColor = (dateRangeIndex) => getSegmentDateRangeColor(
    Color,
    sortIndex,
    dateRangeIndex,
    dateRanges.length,
  );

  const color = !isStaticSegment && SEGMENT_COLORS[sortIndex % SEGMENT_COLORS.length][0];

  const title = isStaticSegment ? <FormattedMessage {...globalMessages.staticSegmentName} /> : friendlyTitle;

  const isEditable = !createdByEmail;

  let sharedTooltip;
  if (shared) {
    if (!isEditable) {
      sharedTooltip = <FormattedMessage {...messages.sharedByTooltip} values={segment} />;
    } else {
      sharedTooltip = <FormattedMessage {...messages.sharedSegmentTooltip} />;
    }
  }

  return (
    <div className="filter-segment">
      <div className="filter-segment__info">
        <div className="filter-segment__info-header">
          <span className="filter-segment__info-header-title">
            {segment.segment && <GlobalOutlined style={{ marginRight: 5 }} />}
            {title}
          </span>
          {sharedTooltip && (
            <Tooltip title={sharedTooltip} placement="top">
              <TeamOutlined />
            </Tooltip>
          )}
          {segment.syncToMatomo && (
            <Tooltip title={<FormattedMessage {...messages.syncToMatomoTooltip} />} placement="top">
              <img className="matomo-icon" src={MatomoIcon} alt="Matomo" />
            </Tooltip>
          )}
          <Tooltip title={switchTooltip} placement="top">
            <Switch
              size="small"
              className="filter-segment__switch"
              checked={isEnabled}
              disabled={switchDisabled}
              loading={toggling}
              onChange={toggleSegment}
            />
          </Tooltip>
        </div>
        {responseCounts?.length > 0 && dateRanges.map((dateRange, index) => {
          const count = responseCounts[index];
          const dateRangePercent = getPercent(count, filterResponseCounts[index]);
          const date = formatDateRange(dayjs, dateRange.period, dateRange.startDate, dateRange.endDate, { weekDates: false, locale: 'en-gb' });

          return (
            <div key={`${id}--${index}`}>
              <div className="filter-segment__info-response-counts">
                <div className="filter-segment__info-response-counts-label">
                  <CalendarOutlined style={{ marginRight: 5 }} />
                  {date}
                </div>
                <div className="filter-segment__info-response-counts-percent">
                  <FormattedMessage {...messages[isStaticSegment ? 'allResultsCount' : 'resultsCount']} values={{ count, percent: dateRangePercent }} />
                </div>
              </div>
              <Progress
                size="small"
                strokeLinecap="square"
                showInfo={false}
                percent={dateRangePercent}
                strokeColor={getProgressbarColor(index)}
              />
            </div>
          );
        })}
        {responseCounts?.length === 0 && isEnabled && (
          <div className="filter-segment__info-response-counts-label" style={{ color: 'red' }}>
            <WarningOutlined style={{ marginRight: 5 }} />
            Invalid segment
          </div>
        )}
      </div>

      {!isStaticSegment && (
      <Button
        type="primary"
        className="filter-segment__edit"
        size="small"
        block
        onClick={() => onEdit(id)}
        style={{ backgroundColor: color, borderColor: color }}
      >
        <FormattedMessage {...globalMessages[isEditable ? 'edit' : 'viewDetails']} />
      </Button>
      )}
    </div>
  );
};

export default Segment;
